import * as Sentry from "@sentry/react";

import { isStaleChunkError } from "./chunk-errors";

/**
 * Inicializa el SDK de Sentry leyendo el DSN desde la env var
 * `VITE_SENTRY_DSN`. La función vive aquí para que el bootstrap
 * en `main.tsx` quede minimal y para concentrar la política de
 * scrub de PII en un solo lugar.
 *
 * Reglas materializadas (Principio VIII + II):
 *   - Sin logs aplicativos: NO usamos console.* en runtime productivo.
 *   - Errores → Sentry vía ErrorBoundary + capturas explícitas.
 *   - DSN inyectado por env (NO hardcodeado).
 *   - `beforeSend` y `beforeBreadcrumb` filtran PII y secretos.
 *   - Si el DSN está vacío y APP_ENV es producción, fallamos visiblemente
 *     (lanzamos error) — sin observabilidad ≠ correr a ciegas.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const env = import.meta.env.MODE ?? "development";

  if (!dsn) {
    if (env === "production") {
      throw new Error(
        "VITE_SENTRY_DSN es obligatorio en builds productivos (Principio VIII).",
      );
    }
    // Dev local sin DSN: el SDK queda inactivo.
    return;
  }

  Sentry.init({
    dsn,
    environment: env,
    // Performance/INP tracing re-activado al 10%. Se había apagado mientras se
    // depuraba el freeze de 17-26s (el click listener de Sentry aparecía en el
    // LoAF), pero la causa raíz era el render-loop de RequirePermission (ya
    // corregido), NO Sentry. 10% da observabilidad de performance con overhead
    // bajo. Replays siguen apagados (no los necesitamos).
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event) {
      if (isStaleChunkNoise(event)) return null;
      return scrubEvent(event);
    },
    beforeBreadcrumb(breadcrumb) {
      return scrubBreadcrumb(breadcrumb);
    },
  });
}

/**
 * isStaleChunkNoise — adapta el helper compartido `isStaleChunkError`
 * (de `lib/chunk-errors.ts`) al shape de `Sentry.ErrorEvent`. Drop en
 * BeforeSend: errores inherentes a SPA + lazy chunks post-deploy NO
 * son bugs reales y solo ensucian el inbox.
 */
function isStaleChunkNoise(event: Sentry.ErrorEvent): boolean {
  if (event.message && isStaleChunkError(event.message)) return true;
  for (const ex of event.exception?.values ?? []) {
    if (ex.value && isStaleChunkError(ex.value)) return true;
    if (ex.type && isStaleChunkError(ex.type)) return true;
  }
  return false;
}

/**
 * Regex que matchea (case-insensitive) cualquier nombre de campo
 * que contenga password / secret / token / api(_-)?key / cookie /
 * authorization. Cubre tanto headers como keys de body o
 * breadcrumb data.
 */
const SENSITIVE_KEY_RE =
  /(password|secret|token|api[_-]?key|cookie|authorization)/i;

const REDACTED = "[redacted]";

// Guardas defensivos del scrub recursivo (root cause del freeze de 17-26s en
// staging — LoAF apuntó al click listener de Sentry). SIN estos límites, si
// Sentry pasaba a beforeSend/beforeBreadcrumb un objeto con ciclos o un grafo
// enorme (un fiber de React, un nodo DOM, el evento), scrubObject recorría el
// grafo completo sin fin → bloqueo síncrono del main thread. El cap de
// profundidad + el WeakSet anti-ciclos lo acotan; Sentry igual normaliza a
// profundidad 3 al serializar, así que no se pierde info útil.
const SCRUB_MAX_DEPTH = 6;

/**
 * Recorre un objeto recursivamente. Reemplaza por REDACTED cualquier
 * value cuya key matchee SENSITIVE_KEY_RE. Acotado por profundidad +
 * detección de ciclos para no colgarse ante grafos cíclicos/enormes.
 */
export function scrubObject(
  input: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (input === null || input === undefined) return input;
  if (typeof input !== "object") return input;
  if (depth >= SCRUB_MAX_DEPTH) return "[Truncated]";
  if (seen.has(input)) return "[Circular]";
  seen.add(input);

  if (Array.isArray(input)) {
    return input.map((item) => scrubObject(item, depth + 1, seen));
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    if (SENSITIVE_KEY_RE.test(k)) {
      out[k] = REDACTED;
    } else {
      out[k] = scrubObject(v, depth + 1, seen);
    }
  }
  return out;
}

/** Reemplaza values en query strings cuando la key es sensible. */
function scrubQueryString(qs: string | undefined): string | undefined {
  if (!qs) return qs;
  return qs
    .split("&")
    .map((pair) => {
      const eq = pair.indexOf("=");
      if (eq < 0) return pair;
      const key = pair.slice(0, eq);
      if (SENSITIVE_KEY_RE.test(key)) {
        return `${key}=${REDACTED}`;
      }
      return pair;
    })
    .join("&");
}

/** Best-effort scrub de body string que parece JSON. */
function scrubJsonStringBody(body: string): string {
  return body.replace(
    /"(password|secret|token|api[_-]?key|cookie|authorization)"\s*:\s*"[^"]*"/gi,
    `"$1": "${REDACTED}"`,
  );
}

function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // Mutate in place; los TypeScript types de Sentry tienen
  // exactOptionalPropertyTypes-incompatibilidades que rodeamos con
  // any-narrow scopes locales.
  const request = event.request;
  if (request) {
    if (request.headers) {
      const headers = request.headers as Record<string, string>;
      for (const k of Object.keys(headers)) {
        if (SENSITIVE_KEY_RE.test(k)) {
          headers[k] = REDACTED;
        }
      }
    }
    // cookies puede ser string|Record en distintas versiones del SDK;
    // borrarla es más seguro que sobrescribirla.
    delete (request as { cookies?: unknown }).cookies;
    if (typeof request.query_string === "string") {
      const scrubbed = scrubQueryString(request.query_string);
      if (typeof scrubbed === "string") {
        request.query_string = scrubbed;
      }
    }
    if (request.data) {
      if (typeof request.data === "string") {
        request.data = scrubJsonStringBody(request.data);
      } else if (typeof request.data === "object") {
        request.data = scrubObject(request.data);
      }
    }
  }

  if (event.contexts) {
    const scrubbed = scrubObject(event.contexts);
    if (scrubbed && typeof scrubbed === "object") {
      event.contexts = scrubbed as typeof event.contexts;
    }
  }

  if (event.tags) {
    for (const k of Object.keys(event.tags)) {
      if (SENSITIVE_KEY_RE.test(k)) {
        event.tags[k] = REDACTED;
      }
    }
  }

  return event;
}

function scrubBreadcrumb(
  bc: Sentry.Breadcrumb,
): Sentry.Breadcrumb | null {
  if (bc.data) {
    const scrubbed = scrubObject(bc.data);
    if (scrubbed && typeof scrubbed === "object") {
      bc.data = scrubbed as typeof bc.data;
    }
  }
  return bc;
}

export { Sentry };
